import { MatchActions } from '../../enums/array-compare/match-actions';
import { ResultTypeArrayCompare } from '../../enums/array-compare/result-type-array-compare';
import { DestinationTaskConfigBase } from '../config-objects/destination-service-config-base';
import { IfRuleGroup } from '../if-gate/if-rule-group';

/**
 * Configuration for an ArrayComparison task — compares two arrays within processingData
 * using IfRuleGroup matching rules and routes matched/unmatched items separately.
 * Both collection fields are removed from processingData before being spread into matched records.
 * ruleGroup value1/value2 must use "<collectionField>.<itemField>" path format.
 */
export class ArrayComparisonConfig extends DestinationTaskConfigBase {
	/**
	 * Dot-notation path within processingData to the first (primary) array.
	 * @default ''
	 */
	collectionField1 = '';

	/**
	 * Dot-notation path within processingData to the second (comparison) array.
	 * @default ''
	 */
	collectionField2 = '';

	/**
	 * How the two collections are combined based on the ruleGroup match.
	 * CombineMatchingRecords=0, CombineMatchingIndexes=1,
	 * OnlyKeepMatchingRecordsFromCollectionA=2, OnlyKeepMatchingRecordsFromCollectionB=3,
	 * OnlyKeepNonMatchingRecords=4, OnlyKeepNonMatchingRecordsFromCollectionA=5,
	 * OnlyKeepNonMatchingRecordsFromCollectionB=6.
	 * @default MatchActions.CombineMatchingRecords
	 */
	matchAction: MatchActions = MatchActions.CombineMatchingRecords;

	/**
	 * Output shape. ResultPerRecord=0 (one record per matched item),
	 * ArrayOfResults=1 (all matched items in a single { payload: [] } record).
	 * @default ResultTypeArrayCompare.ResultPerRecord
	 */
	resultType: ResultTypeArrayCompare = ResultTypeArrayCompare.ResultPerRecord;

	/**
	 * Matching conditions using IfRuleGroup structure. rule.value1 must use
	 * "<collectionField1>.<itemField>" format; rule.value2 uses "<collectionField2>.<itemField>".
	 * Not required when matchAction=CombineMatchingIndexes.
	 */
	ruleGroup: IfRuleGroup = new IfRuleGroup();

	/**
	 * Routing reference to the task that receives the unmatched remainder record.
	 * The remainder always contains unmatched items from both collections.
	 * Managed by the flow builder.
	 * @default ''
	 */
	remainingDestinationID = '';

	/** UI-only property. Not used at runtime. */
	ruleItems: any[] = [];

	constructor(init?: Partial<ArrayComparisonConfig>) {
		super(init);
		if (init) {
			Object.assign(this, init);
		}
	}
}
